import { useState } from 'react';
import styled from 'styled-components';
import {
  ContainerMarginSections,
  DisplayTitle,
  SectionLabel,
  Colors,
  fonts,
} from '../style/GlobalStyles';

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
  margin-bottom: 3.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const HeadLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Nav = styled.div`
  display: flex;
  gap: 0.8rem;
`;

const ArrowBtn = styled.button`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 1.5px solid ${Colors.ink};
  background: ${({ $filled }) => ($filled ? Colors.ink : 'transparent')};
  color: ${({ $filled }) => ($filled ? Colors.paper : Colors.ink)};
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s, transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
  }
`;

const Viewport = styled.div`
  overflow: hidden;
`;

const Track = styled.div`
  display: flex;
  gap: 0;
  transition: transform 0.5s cubic-bezier(0.65, 0, 0.35, 1);
  transform: translateX(-${({ $index, $size }) => $index * $size}%);
`;

const Card = styled.div`
  flex: 0 0 33.3333%;
  padding: 0 2.5rem;
  border-left: 1px solid ${Colors.line};

  &:first-child {
    border-left: none;
    padding-left: 0;
  }

  @media (max-width: 980px) {
    flex-basis: 50%;
  }

  @media (max-width: 680px) {
    flex-basis: 100%;
    padding: 0;
    border-left: none;
  }
`;

const Rating = styled.div`
  font-family: ${fonts.body};
  font-size: 0.9rem;
  color: ${Colors.muted};
  margin-bottom: 2.5rem;
`;

const Quote = styled.p`
  font-family: ${fonts.body};
  font-size: 1.05rem;
  line-height: 1.7;
  color: ${Colors.ink};
  min-height: 160px;
  margin: 0 0 2rem 0;
`;

const Testimonials = ({ testimonials = [] }) => {
  const [index, setIndex] = useState(0);
  const maxIndex = Math.max(0, testimonials.length - 3);

  return (
    <ContainerMarginSections>
      <Head>
        <HeadLeft>
          <SectionLabel className="gsap-fade-up">Testimonials</SectionLabel>
          <DisplayTitle className="gsap-fade-up">
            What do
            <br />
            clients say?
          </DisplayTitle>
        </HeadLeft>
        <Nav className="gsap-fade-up">
          <ArrowBtn
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0}
            aria-label="Previous"
          >
            ←
          </ArrowBtn>
          <ArrowBtn
            $filled
            onClick={() => setIndex((i) => Math.min(maxIndex, i + 1))}
            disabled={index >= maxIndex}
            aria-label="Next"
          >
            →
          </ArrowBtn>
        </Nav>
      </Head>

      <Viewport className="gsap-fade-up">
        <Track $index={index} $size={33.3333}>
          {testimonials.map((t, i) => (
            <Card key={i}>
              <Rating>{t.rating || '5/5'}</Rating>
              <Quote>“{t.quote}”</Quote>
            </Card>
          ))}
        </Track>
      </Viewport>
    </ContainerMarginSections>
  );
};

export default Testimonials;
